import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useEffectOnce } from 'react-use'

import { queryClient } from '@/api/api'
import {
  assignSpecificTaskToUserOptions,
  listAssignableTasksForUserOptions,
} from '@/api/codegen/@tanstack/react-query.gen'
import { useAssignSpecificTaskToUserQuery } from '@/api/domain/task/task'
import { TaskAutoReleasedModal } from '@/components/modals/taskAutoReleasedModal/TaskAutoReleasedModal'
import { Loading } from '@/components/ui/Loading'
import { useSidebar } from '@/components/ui/Sidebar'
import { SidebarKeyMap } from '@/constants/sidebar.constants'
import { SolutionPage, hasSolution } from '@/modules/client-relo'
import { TaskBasicPage } from '@/pages/task-basic/TaskBasic.page'
import { TaskVideoAnnotationPage } from '@/pages/task-objectTracking/TaskVideoAnnotation.page'

const TaskPageContainer = () => {
  const { setOpen } = useSidebar(SidebarKeyMap.MAIN)
  const navigate = useNavigate()
  const { taskId } = useParams({ from: '/_auth/task-list/$taskId' })

  const {
    data: task,
    isFetching,
    isPending,
    isError,
  } = useAssignSpecificTaskToUserQuery(taskId)

  // close sidebar on mount and open it on unmount
  useEffectOnce(() => {
    setOpen(false)

    return () => {
      setOpen(true)
      queryClient.invalidateQueries({
        queryKey: assignSpecificTaskToUserOptions({
          path: {
            taskID: taskId,
          },
        }).queryKey,
      })
    }
  })

  if (isFetching || isPending) {
    return <Loading />
  }

  if (isError) {
    navigate({
      to: '/dashboard',
    })

    return null
  }

  const onAfterSubmit = () => {
    queryClient.invalidateQueries({
      queryKey: listAssignableTasksForUserOptions().queryKey,
    })
    navigate({
      to: '/task-list',
    })
  }

  let taskPage: React.ReactNode

  if (task.type === 'video_annotation') {
    taskPage = (
      <TaskVideoAnnotationPage
        task={task}
        isNewTaskModalEnable={false}
        onAfterSubmit={onAfterSubmit}
      />
    )
  } else if (hasSolution(task)) {
    taskPage = (
      <SolutionPage
        task={task}
        isNewTaskModalEnable={false}
        onAfterSubmit={onAfterSubmit}
      />
    )
  } else {
    taskPage = (
      <TaskBasicPage
        task={task}
        isNewTaskModalEnable={false}
        onAfterSubmit={onAfterSubmit}
      />
    )
  }

  return (
    <>
      {taskPage}
      <TaskAutoReleasedModal currentTaskId={task.id} />
    </>
  )
}

export const Route = createFileRoute('/_auth/task-list/$taskId')({
  beforeLoad: () => {
    // TODO: we should check if the user has access to the task
  },
  component: TaskPageContainer,
  loader: ({ params }) => ({ breadcrumbTitle: `Task (${params.taskId})` }),
})
